/**
 * 业务动作统一执行器
 *
 * 后端业务接口尚未就绪（返回 404 / 无法连接）时：
 *   - 明确提示「本次操作仅在本地演示，未写入后端」，绝不假装成功；
 *   - 让页面本地更新状态，保证交互流程可以完整走查。
 * 后端接口上线后，同一段页面代码会自动走真实请求，无需修改。
 */

import { ApiError, isUnimplementedError } from '@/api/client';
import { feedback } from './feedback';

export interface PerformOptions<T> {
  /** 真实接口调用 */
  action: () => Promise<T>;
  /**
   * 成功提示。传空串表示"不弹"——批量场景逐条弹会刷屏，
   * 由调用方在循环结束后给一句汇总的回执。
   *
   * 也可以传一个函数，用响应数据拼提示（如"已保存为 v3"）：
   * 后端已经把这件事说清楚了，硬编码一句笼统的话会把信息丢掉。
   */
  successMessage: string | ((data: T) => string);
  /**
   * 接口未就绪时的本地兜底（用于演示）。
   * 注意：真实业务错误（权限不足、状态冲突等）不会触发兜底。
   */
  offline?: () => void;
  /** 接口未就绪时的提示文案 */
  offlineMessage?: string;
}

export interface PerformResult<T> {
  ok: boolean;
  /** 走的是本地演示兜底 */
  offline: boolean;
  data: T | null;
  error: ApiError | Error | null;
}

export async function performAction<T>(options: PerformOptions<T>): Promise<PerformResult<T>> {
  const { action, successMessage, offline, offlineMessage } = options;
  try {
    const data = await action();
    const text = typeof successMessage === 'function' ? successMessage(data) : successMessage;
    // 空文案不弹：`message.success('')` 会渲染成一个只有图标的空气泡，
    // 批量操作里就是几十个空提示糊在屏幕上（见 PerformOptions.successMessage）
    if (text) feedback.success(text);
    return { ok: true, offline: false, data, error: null };
  } catch (err) {
    if (offline && isUnimplementedError(err)) {
      offline();
      feedback.warning(
        offlineMessage ??
          '后端业务接口尚未就绪，本次操作仅在本地演示，未写入数据库（接口上线后自动生效）',
      );
      return { ok: true, offline: true, data: null, error: null };
    }
    const error = err instanceof Error ? err : new Error('操作失败');
    if (err instanceof ApiError) {
      feedback.error(error.message);
    } else {
      feedback.error(error.message);
    }
    return { ok: false, offline: false, data: null, error };
  }
}

export default performAction;
